import { Input, Select, Steps, Tooltip } from 'antd';
import React from 'react';
import { ReactComponent as CreateIcon } from 'assets/svg/create.svg';
import { ReactComponent as WaitingIcon } from 'assets/svg/waiting.svg';
import { ReactComponent as ApprovedIcon } from 'assets/svg/approved.svg';
import { ReactComponent as RejectIcon } from 'assets/svg/reject.svg';
import { useNavigate } from 'react-router-dom';
import { CATEGORY, LIST_TYPE, PROJECT_STATUS } from 'constants/constants';
import moment from 'moment';
import Page from 'views/common/Page';
import usePage from 'hooks/usePage';
import { checkHasAnyPermission, PERMISSION } from 'constants/permission';
import { useSelector } from 'react-redux';
import configEnv from 'env';
import classNames from 'classnames';

const getStep = (status, pause) => {
  const process1 = {
    status: 'process',
    icon: (
      <span>
        <Tooltip title="Creating">
          <CreateIcon />
        </Tooltip>
      </span>
    ),
  };
  const process2 = {
    status: 'wait',
    icon: (
      <span>
        <Tooltip title="Waiting Approve">
          <WaitingIcon className="waiting-icon" />
        </Tooltip>
      </span>
    ),
  };

  const process3 = {
    status: 'wait',
    icon: (
      <span>
        <Tooltip title="Approved">
          <ApprovedIcon className="approved-icon" />
        </Tooltip>
      </span>
    ),
  };

  if (pause) {
    process1.status = 'error';
    process1.icon = (
      <span>
        <Tooltip title="Pause">
          <RejectIcon className="reject-icon" />
        </Tooltip>
      </span>
    );
    return [process1, process2, process3];
  }

  if (status === PROJECT_STATUS.WAITING_APPROVE) {
    process1.status = 'finish';
    process2.status = 'process';
  }

  if (status === PROJECT_STATUS.APPROVED) {
    process1.status = 'finish';
    process2.status = 'finish';
    process3.status = 'finish';
  }

  if (status === PROJECT_STATUS.REJECT) {
    process1.status = 'finish';
    process2.status = 'finish';
    process3.status = 'error';

    process3.icon = (
      <span>
        <Tooltip title="Reject">
          <RejectIcon className="reject-icon" />
        </Tooltip>
      </span>
    );
  }

  return [process1, process2, process3];
};

const getDate = (date) => {
  if (!date) return;
  return moment(new Date(date)).format('DD/MM/yyyy');
};

const getFirstChar = (v) => {
  return v && v[0];
};

const DEFAULT_PAGE = 12;

const ProjectItem = (props) => {
  const {
    name,
    description,
    id,
    status,
    modifiedDate,
    avatar,
    accessLevel,
    pause,
  } = props;
  const navigate = useNavigate();

  const onClick = () => {
    navigate('/project/' + id);
  };

  return (
    <div className="project-item" onClick={onClick}>
      <div className="project-item__block">
        <div className="project-item__img">
          {avatar ? (
            <img
              src={`${configEnv.STORAGE_BASE_URL}/user/${avatar}`}
              alt="project-a"
            />
          ) : (
            <span>{getFirstChar(name)}</span>
          )}
        </div>
        <div className="project-item__text">
          <div className="project-item__name">{name}</div>
          <div className="project-item__desc">{description}</div>
        </div>
      </div>

      <div className="project-item__process">
        <Steps size="small" items={getStep(status, pause)} />
      </div>
      <div className="project-item__footer">
        <div className="project-item__date">
          <span className="title">Last update:</span>
          <span className="value">{getDate(modifiedDate)}</span>
        </div>
        <div
          className={classNames('project-item__access', {
            'project-item__access--public': accessLevel !== 'PRIVATE',
          })}
        >
          {accessLevel === 'PRIVATE' ? 'Private' : 'Public'}
        </div>
      </div>
    </div>
  );
};

const ManageProject = () => {
  const navigate = useNavigate();
  const {
    status,
    data,
    meta,
    search,
    onChangeFieldText,
    onChangeField,
    onChangePage,
    currentPage,
  } = usePage({
    name: LIST_TYPE.PROJECT,
    pageSize: DEFAULT_PAGE,
  });
  const { permissions } = useSelector((state) => state.auth);

  return (
    <Page
      title="Project"
      status={status}
      data={data}
      currentPage={currentPage}
      pageSize={DEFAULT_PAGE}
      onChangePage={onChangePage}
      onCreate={
        checkHasAnyPermission(permissions, [PERMISSION.CREATE_DCS])
          ? () => navigate('/project/create')
          : null
      }
      meta={meta}
      renderSearch={() => (
        <div className="page-search">
          <Input
            className="page-search__input"
            placeholder="Search for id, name and description"
            value={search.query}
            onChange={(e) => onChangeFieldText(e, 'query')}
            size="large"
          />
          <Select
            className="page-search__select"
            placeholder="Status"
            allowClear
            value={search.status}
            onChange={(v) => onChangeField(v, 'status')}
            size="large"
            options={[
              {
                label: 'Creating',
                value: PROJECT_STATUS.CREATING,
              },
              {
                label: 'Waiting Approve',
                value: PROJECT_STATUS.WAITING_APPROVE,
              },
              {
                label: 'Approved',
                value: PROJECT_STATUS.APPROVED,
              },
              {
                label: 'Reject',
                value: PROJECT_STATUS.REJECT,
              },
            ]}
          />
          <Select
            className="page-search__select"
            placeholder="Category"
            allowClear
            value={search.category}
            onChange={(v) => onChangeField(v, 'category')}
            size="large"
            options={Object.keys(CATEGORY).map((key) => ({
              label: CATEGORY[key].name,
              value: key,
            }))}
          />
          <Select
            className="page-search__select"
            placeholder="Access level"
            allowClear
            value={search.accessLevel}
            onChange={(v) => onChangeField(v, 'accessLevel')}
            size="large"
            options={[
              {
                label: 'Private',
                value: 'PRIVATE',
              },
              {
                label: 'Public',
                value: 'PUBLIC',
              },
            ]}
          />
        </div>
      )}
      renderList={() => (
        <div className="list-item-project">
          {data.map((item) => (
            <ProjectItem key={item.id} {...item} />
          ))}
        </div>
      )}
    />
  );
};

export default ManageProject;
